import { useState } from 'react';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

const products = [
  {
    id: 1,
    supabaseId: '2e0044ed-1e29-4cd6-ab81-391c6dfc34dc',
    icon: 'ri-book-2-line',
    badge: 'BEST SELLER',
    badgeColor: 'bg-yellow-400 text-black',
    title: 'Top 50 Roblox Secrets Guide',
    description: 'Discover hidden rooms, secret tricks and crazy Roblox secrets that 99% of players have never seen.',
    price: 29,
    glow: 'rgba(168,85,247,0.5)',
    borderColor: 'border-purple-500/60',
    hasStripe: true,
    image: 'https://public.readdy.ai/ai/img_res/242e4495ead71f78e82e47044c738a85.jpg',
  },
  {
    id: 2,
    supabaseId: 'daa9f8aa-c972-4611-a11c-f53b81183c30',
    icon: 'ri-map-2-line',
    badge: 'POPULAR',
    badgeColor: 'bg-purple-600 text-white',
    title: 'Hidden Rooms Map Pack',
    description: 'Exclusive locations and secret maps revealing the most hidden areas inside Roblox worlds.',
    price: 12,
    glow: 'rgba(168,85,247,0.4)',
    borderColor: 'border-purple-700/50',
    hasStripe: true,
    image: 'https://readdy.ai/api/search-image?query=dark%20mysterious%20treasure%20map%20with%20glowing%20neon%20markings%2C%20secret%20locations%20marked%20with%20yellow%20X%20symbols%2C%20dark%20parchment%20style%20gaming%20map%20with%20purple%20glow%20effects&width=400&height=280&seq=product-map-001&orientation=landscape',
  },
  {
    id: 3,
    supabaseId: '9221386e-692a-44bb-959d-eb19d099fc1b',
    icon: 'ri-flashlight-line',
    badge: 'ADVANCED',
    badgeColor: 'bg-yellow-400 text-black',
    title: 'Pro Tricks & Glitches Guide',
    description: 'Learn advanced tricks to unlock hidden areas and master secret glitches that pros use.',
    price: 19,
    glow: 'rgba(250,204,21,0.4)',
    borderColor: 'border-yellow-500/50',
    hasStripe: true,
    image: 'https://readdy.ai/api/search-image?query=dark%20gaming%20tricks%20guide%20with%20lightning%20bolt%20neon%20effects%2C%20glitch%20visual%20effects%20with%20yellow%20and%20purple%20glow%2C%20advanced%20gaming%20secrets%20digital%20product%20cover%2C%20mysterious%20dark%20background&width=400&height=280&seq=product-tricks-001&orientation=landscape',
  },
];

export default function ProductsSection() {
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUnlock = async (product: typeof products[0]) => {
    if (!product.hasStripe || !product.supabaseId) return;

    setLoadingId(product.id);
    setError(null);

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.supabaseId,
          successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/#products`,
        }),
      });

      const data = await res.json();

      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        setError('Could not start checkout. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section id="products" className="bg-[#0a0a0f] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-purple-500/50 mb-4"
            style={{ background: 'rgba(168,85,247,0.12)' }}
          >
            <i className="ri-fire-line text-yellow-400 text-sm"></i>
            <span className="text-yellow-400 text-xs font-black uppercase tracking-widest">Digital Products</span>
          </div>
          <h2
            className="font-black text-white text-3xl md:text-5xl"
            style={{ textShadow: '0 0 30px rgba(168,85,247,0.6)', fontFamily: "'Orbitron', sans-serif" }}
          >
            Unlock Roblox Secrets 🔥
          </h2>
          <p className="text-gray-400 mt-3 text-base md:text-lg max-w-xl mx-auto">
            Instant digital delivery. Start discovering secrets in minutes.
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 text-center text-red-400 text-sm bg-red-900/20 border border-red-500/30 rounded-xl py-3 px-4">
            {error}
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {products.map((product) => (
            <div
              key={product.id}
              className={`relative flex flex-col rounded-2xl border ${product.borderColor} overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] cursor-pointer group`}
              style={{
                background: 'linear-gradient(160deg, #1a1a2e 0%, #0f0f1a 100%)',
                boxShadow: `0 0 0 1px rgba(168,85,247,0.1), 0 8px 32px rgba(0,0,0,0.4)`,
              }}
            >
              {/* Badge */}
              <div className="absolute top-4 right-4 z-10">
                <span className={`${product.badgeColor} text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full`}>
                  {product.badge}
                </span>
              </div>

              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1a1a2e]"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-yellow-400/60"
                    style={{ background: 'rgba(0,0,0,0.6)', boxShadow: `0 0 20px ${product.glow}` }}
                  >
                    <i className={`${product.icon} text-yellow-400 text-3xl`}></i>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col flex-1 p-6">
                <h3
                  className="text-white font-black text-lg md:text-xl mb-2 leading-tight"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                >
                  {product.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed flex-1 mb-4">
                  {product.description}
                </p>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-yellow-400 text-lg font-bold">$</span>
                  <span
                    className="text-yellow-400 font-black text-4xl"
                    style={{ textShadow: '0 0 16px rgba(250,204,21,0.7)', fontFamily: "'Orbitron', sans-serif" }}
                  >
                    {product.price}
                  </span>
                  <span className="text-gray-500 text-sm ml-1">one-time</span>
                </div>

                {/* CTA */}
                <button
                  onClick={() => handleUnlock(product)}
                  disabled={loadingId === product.id}
                  className="w-full flex items-center justify-center gap-2 bg-transparent border-2 border-yellow-400 text-yellow-400 font-black uppercase tracking-wider py-3 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-yellow-400 hover:text-black text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ boxShadow: `0 0 16px ${product.glow}` }}
                >
                  {loadingId === product.id ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="ri-lock-unlock-line"></i>
                      Unlock Now
                    </>
                  )}
                </button>

                {/* Trust */}
                <p className="text-center text-gray-600 text-xs mt-3 flex items-center justify-center gap-1">
                  <i className="ri-flashlight-line text-yellow-600"></i>
                  {product.hasStripe ? 'Secure Stripe Payment · Instant Delivery' : 'Instant Digital Delivery'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom trust bar */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {[
            { icon: 'ri-shield-check-line', text: 'Secure Checkout' },
            { icon: 'ri-flashlight-line', text: 'Instant Access' },
            { icon: 'ri-refresh-line', text: 'Money-back Guarantee' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-yellow-600`}></i>
              </div>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
